/**
 * Request context for the ported ChatOBI service layer.
 *
 * The legacy code built an axios instance from `process.env.REACT_APP_BACKEND_URL`
 * and a bearer token read out of `sessionStorage`. Inside Evo both come from the
 * host: the base URL and auth headers are resolved through the BFF proxy backend
 * (`useBackend`), so they are published here once on mount and read lazily by
 * `Helper/api`.
 */

export type RequestHeaders = Record<string, string>;

type RequestContext = {
  baseUrl: string;
  getHeaders: () => Promise<RequestHeaders>;
  onUnauthorised: () => void;
  /**
   * Subscription `/evo/context` resolved this session to. An organisation can
   * hold several chatbot subscriptions, one chatbot each, and the API resolves
   * the property per request, so every request has to name the same one or it
   * is served from the organisation's first chatbot.
   */
  instanceId: string | null;
};

let context: RequestContext = {
  baseUrl: '',
  getHeaders: async () => ({}),
  onUnauthorised: () => {},
  instanceId: null,
};

export function setRequestContext(next: Partial<RequestContext>) {
  context = { ...context, ...next };
}

export function getBaseUrl() {
  return context.baseUrl;
}

export async function getRequestHeaders(): Promise<RequestHeaders> {
  const headers = await context.getHeaders();
  return context.instanceId
    ? { ...headers, 'X-Evo-Instance-Id': context.instanceId }
    : headers;
}

export function getInstanceId() {
  return context.instanceId;
}

export function notifyUnauthorised() {
  context.onUnauthorised();
}

export function isRequestContextReady() {
  return context.baseUrl !== '';
}
